<script lang="ts" setup>
import StatusTag from '@/common/components/molecules/statusTag/StatusTag.vue';
import LogViewer from '@/kubernetes/components/containerLog/LogViewer.vue';
import AlertTargetList from '@/alert/components/alertTargetList/AlertTargetList.vue';
import AlertStatusButtonRow from '@/alert/components/alertStatusButtonRow/AlertStatusButtonRow.vue';
import BaseChipsCellWithTooltip from '@/common/components/molecules/baseChipsCellWithTooltip/BaseChipsCellWithTooltip.vue';
import type { NotificationType } from '@/alert/utils/notification.types';
import type { OccurrenceItem } from './eventAlertInformation.types';
import {
  isTextOccurrenceItem,
  isListOccurrenceItem,
  isStatusOccurrenceItem,
  isNotificationStatusOccurrenceItem,
  isFilterConditionsOccurrenceItem,
  isLogViewerOccurrenceItem,
  isTagListOccurrenceItem,
  isTargetListOccurrenceItem,
  isThresholdOccurrenceItem,
  isAlertFilterOccurrenceItem,
  isDisplayColumnsOccurrenceItem,
} from './eventAlertInformation.utils';
import ThresholdDisplay from './ThresholdDisplay.vue';

interface Props {
  item: OccurrenceItem;
  index: number;
  contentItemMinWidth: string;
}

interface Emit {
  (e: 'click-status-icon', iconType: NotificationType): void;
}

defineProps<Props>();
const emit = defineEmits<Emit>();
</script>

<template>
  <!-- logViewer 타입 -->
  <h-stack
    v-if="isLogViewerOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item event-alert-occurrence-message__alert-value-viewer"
    :spacing="20"
    align="start"
  >
    <span>{{ item.label }}</span>
    <log-viewer
      :previous="false"
      :use-external-btn="false"
      :display-log-time="false"
      :display-log-title="false"
      :log="[
        {
          timestamp: '',
          log: String(item.value ?? ''),
        },
      ]"
    />
  </h-stack>

  <!-- text 타입 -->
  <h-stack
    v-else-if="isTextOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item"
    :spacing="20"
  >
    <span>{{ item.label }}</span>
    <span>{{ item.value }}</span>
  </h-stack>

  <!-- list 타입 -->
  <h-stack
    v-else-if="isListOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item list-occurrence-item"
    :spacing="20"
  >
    <span>
      {{ item.label }}
      <span
        v-if="item.labelBadge"
        class="event-alert-occurrence-message__label-badge"
      >
        {{ item.labelBadge }}
      </span>
    </span>
    <h-stack
      v-if="item.listRenderType === 'separator'"
      :spacing="8"
    >
      <template
        v-for="(v, listIndex) in item.list"
        :key="listIndex"
      >
        <span>{{ v }}</span>
        <separator
          v-if="listIndex < (item.list?.length ?? 0) - 1"
          class="event-alert-rule-data__separator"
        />
      </template>
    </h-stack>
    <base-chips-cell-with-tooltip
      v-else
      :chips="item.list"
      :row-index="index"
      :use-wheel-event="false"
    />
  </h-stack>

  <!-- notificationStatus 타입 -->
  <h-stack
    v-else-if="isNotificationStatusOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item"
    :spacing="20"
  >
    <span>{{ item.label }}</span>
    <alert-status-button-row
      :status-list="item.notificationStatus"
      :row-index="0"
      @click-status-button="(methodType) => emit('click-status-icon', methodType)"
    />
  </h-stack>

  <!-- status 타입 -->
  <h-stack
    v-else-if="isStatusOccurrenceItem(item) && item.statusType"
    class="event-alert-occurrence-message__content-item"
    :spacing="20"
  >
    <span>{{ item.label }}</span>
    <status-tag :type="item.statusType">{{ item.value }}</status-tag>
  </h-stack>

  <!-- filterConditions 타입 -->
  <h-stack
    v-else-if="isFilterConditionsOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item"
    :spacing="20"
  >
    <span>
      {{ item.label }}
      <span
        v-if="item.labelBadge"
        class="event-alert-occurrence-message__label-badge"
      >
        {{ item.labelBadge }}
      </span>
    </span>
    <h-stack :spacing="8">
      <template
        v-for="(condition, conditionIndex) in item.filterConditions"
        :key="conditionIndex"
      >
        <h-stack :spacing="8">
          <span>{{ condition.columnName }}</span>
          <span>{{ condition.operator }}</span>
          <span>{{ condition.value }}</span>
        </h-stack>
        <separator
          v-if="conditionIndex < (item.filterConditions?.length ?? 0) - 1"
          class="event-alert-rule-data__separator"
        />
      </template>
    </h-stack>
  </h-stack>

  <!-- tagList 타입 -->
  <h-stack
    v-else-if="isTagListOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item list-occurrence-item"
    :spacing="20"
  >
    <span>{{ item.label }}</span>
    <alert-target-list
      :target-list="item.tags ?? []"
      :is-expand="true"
    />
  </h-stack>

  <!-- targetList 타입 -->
  <h-stack
    v-else-if="isTargetListOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item list-occurrence-item"
    :spacing="20"
  >
    <span>{{ item.label }}</span>
    <alert-target-list
      :target-list="item.targets ?? []"
      :is-expand="true"
      :is-clickable="true"
    />
  </h-stack>

  <!-- threshold 타입 -->
  <h-stack
    v-else-if="isThresholdOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item avg-value"
    :spacing="20"
  >
    <span>{{ item.label }}</span>
    <threshold-display
      v-if="item.threshold"
      :evaluate-value="item.threshold.evaluateValue"
      :during-time-interval="item.threshold.duringTimeInterval"
      :comparator="item.threshold.comparator"
      :warning-value="item.threshold.warningValue"
      :critical-value="item.threshold.criticalValue"
      :unit="item.threshold.unit"
      :show-label="false"
    />
  </h-stack>

  <!-- alertFilter 타입 -->
  <h-stack
    v-else-if="isAlertFilterOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item"
    :spacing="20"
  >
    <span>
      {{ item.label }}
      <span
        v-if="item.alertFilter?.operator"
        class="event-alert-occurrence-message__label-badge"
      >
        {{ item.alertFilter.operator }}
      </span>
    </span>
    <h-stack
      v-if="item.alertFilter && item.alertFilter.items && item.alertFilter.items.length > 0"
      :spacing="8"
    >
      <template
        v-for="(filterItem, filterIdx) in item.alertFilter.items"
        :key="filterIdx"
      >
        <span>{{ filterItem.field }} {{ filterItem.evaluate }} {{ filterItem.value }}</span>
        <separator
          v-if="filterIdx < (item.alertFilter.items?.length ?? 0) - 1"
          class="event-alert-rule-data__separator"
        />
      </template>
    </h-stack>
  </h-stack>

  <!-- displayColumns 타입 -->
  <h-stack
    v-else-if="isDisplayColumnsOccurrenceItem(item)"
    class="event-alert-occurrence-message__content-item"
    :spacing="20"
  >
    <span>{{ item.label }}</span>
    <h-stack
      v-if="item.displayColumns && item.displayColumns.length > 0"
      :spacing="8"
    >
      <template
        v-for="(colItem, colIdx) in item.displayColumns"
        :key="colIdx"
      >
        <span>{{ colItem.alias ? `${colItem.alias}(${colItem.column})` : colItem.column }}</span>
        <separator
          v-if="colIdx < (item.displayColumns?.length ?? 0) - 1"
          class="event-alert-rule-data__separator"
        />
      </template>
    </h-stack>
  </h-stack>
</template>

<style scoped lang="scss">
.event-alert-rule-data__separator {
  width: 1px;
  height: 12px;
  background-color: var(--color-gray-05-05);
}
.event-alert-occurrence-message__content-item {
  height: 20px;

  &.list-occurrence-item {
    align-items: flex-start;
    height: auto;
    min-height: 20px;

    > span:first-of-type {
      margin-top: 6px;
    }

    :deep(.base-grid__chips) {
      width: 100%;
    }

    :deep(.tag-item) {
      /* 인라인 100px 제한만 해제하고 컨테이너 폭에서 ellipsis 되도록 100% 유지 */
      max-width: 100% !important;
      margin: 4px 4px 4px 0;
    }
  }

  .event-alert__tag-list {
    flex: 1;
    flex-wrap: wrap;
    min-width: 0;
    min-height: 24px;
  }

  :deep(.target-list) {
    flex: 1;
    min-width: 0;
  }

  > span:first-of-type {
    flex-shrink: 0;
    min-width: v-bind('contentItemMinWidth');
    font-weight: 500;
  }

  &.avg-value {
    align-items: flex-start;
    height: auto;
    min-height: 38px;

    :deep(.threshold-display) {
      flex: 1;
      min-width: 0;
    }
  }
}
.event-alert-occurrence-message__label-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  margin-left: 4px;
  border-radius: 30px;
  background-color: var(--color-gray-05-03);
  font-size: 11px;
}
.event-alert-occurrence-message__alert-value-viewer {
  height: auto;

  > span:first-of-type {
    margin-top: 12px;
  }

  :deep(.text-viewer) {
    flex: 1;
  }

  :deep(.log-viewer__toolbar) {
    display: none;
  }

  :deep(.log-viewer) {
    min-width: 0;
  }
}
.detail-info-table {
  width: 100%;
  border-spacing: 0;
  border-top: 1px solid var(--grid-border-color);
  border-left: 1px solid var(--grid-border-color);

  th,
  td {
    padding: 0 8px;
    border-right: 1px solid var(--grid-border-color);
    border-bottom: 1px solid var(--grid-border-color);
  }

  th {
    height: 40px;
    background-color: var(--grid-header-bg-color);
  }

  td {
    height: 32px;
  }

  .align-right {
    text-align: right;
  }

  .align-center {
    text-align: center;
  }
}
</style>
